import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star, ExternalLink, Quote } from 'lucide-react';
import { Button } from './ui/button';
import { BACKEND_URL } from '../config';

const API = `${BACKEND_URL}/api`;

const StarRow = ({ rating = 0, size = 'w-4 h-4' }) => {
  const r = Math.round(rating || 0);
  return (
    <div className="flex items-center" aria-label={`${rating} out of 5 stars`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          className={`${size} ${i < r ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );
};

const GoogleG = ({ className }) => (
  <svg className={className} viewBox="0 0 48 48" role="img" aria-label="Google">
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
  </svg>
);

const GoogleReviews = ({ variant = 'light' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/reviews/google`)
      .then((res) => {
        if (mounted) setData(res.data);
      })
      .catch((err) => {
        if (mounted) setError(err?.response?.data?.detail || 'Unable to load reviews');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const isDark = variant === 'dark';
  const sectionBg = isDark
    ? 'bg-gray-900 text-white'
    : 'bg-gradient-to-br from-gray-50 to-white';
  const cardBg = isDark
    ? 'bg-white/10 backdrop-blur-sm border border-white/20 text-white'
    : 'bg-white border border-gray-100 shadow-md hover:shadow-xl transition-shadow';
  const subtle = isDark ? 'text-white/70' : 'text-gray-500';
  const bodyText = isDark ? 'text-white/90' : 'text-gray-700';
  const heading = isDark ? 'text-white' : 'text-gray-900';

  // While loading, or if the reviews API is unavailable, show nothing: the
  // "4.8 / 5 on Google" chip in the video-reviews section already links to Google.
  if (loading || error || !data || !data.reviews || data.reviews.length === 0) return null;

  return (
    <section className={`vk-home vk-section ${sectionBg}`} data-testid="google-reviews-section">
      <div className="vk-wrap">
        <div>
          {/* Header */}
          <header className="vk-head" style={{ marginBottom: 32 }}>
            <div>
              <p className="vk-kicker" style={{ display: 'flex', alignItems: 'center', gap: 10 }}><GoogleG className="h-6 w-6" /> Reviews from Google</p>
              <h2 className="vk-display vk-h2">What our travellers say</h2>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <StarRow rating={data.rating} size="w-5 h-5" />
              <span className={`text-xl font-bold ${heading}`}>{data.rating?.toFixed(1)}</span>
              <span className={subtle}>· {data.user_rating_count} Google reviews</span>
            </div>
          </header>

          {/* Reviews Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {data.reviews.map((r, idx) => (
              <article
                key={r.review_uri || idx}
                className={`${cardBg} rounded-2xl p-6 flex flex-col`}
                data-testid={`google-review-card-${idx}`}
              >
                {/* Rating + Quote icon */}
                <div className="flex items-start justify-between mb-4">
                  <StarRow rating={r.rating} />
                  <Quote className={`w-6 h-6 ${isDark ? 'text-white/30' : 'text-orange-200'}`} />
                </div>

                {/* Review text */}
                <p className={`${bodyText} text-sm leading-relaxed mb-6 flex-1`}>
                  "{r.text?.length > 240 ? r.text.slice(0, 240) + '…' : r.text}"
                </p>

                {/* Author + source link */}
                <div className={`flex items-center gap-3 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                  {r.author_photo ? (
                    <img
                      src={r.author_photo}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold">
                      {(r.author_name || 'G').charAt(0)}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {r.author_uri ? (
                      <a
                        href={r.author_uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`font-semibold text-sm block truncate hover:underline ${heading}`}
                      >
                        {r.author_name || 'Google reviewer'}
                      </a>
                    ) : (
                      <span className={`font-semibold text-sm block truncate ${heading}`}>
                        {r.author_name || 'Google reviewer'}
                      </span>
                    )}
                    <span className={`text-xs ${subtle}`}>{r.relative_time}</span>
                  </div>
                  {r.review_uri && (
                    <a
                      href={r.review_uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="View this review on Google Maps"
                      className={`${subtle} hover:text-orange-600 transition-colors`}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </article>
            ))}

            {/* 6th slot — "Write a review" CTA card */}
            <article
              className={`${cardBg} rounded-2xl p-6 flex flex-col items-center justify-center text-center relative overflow-hidden`}
              data-testid="google-review-write-cta-card"
            >
              {/* Google-color accent ring */}
              <div className="absolute inset-x-0 top-0 h-1 flex">
                <div className="flex-1" style={{ background: '#4285F4' }} />
                <div className="flex-1" style={{ background: '#EA4335' }} />
                <div className="flex-1" style={{ background: '#FBBC05' }} />
                <div className="flex-1" style={{ background: '#34A853' }} />
              </div>

              <GoogleG className="h-12 w-12 mb-4" />
              <h3 className={`text-xl font-bold mb-2 ${heading}`}>
                Travelled with us?
              </h3>
              <p className={`${bodyText} text-sm mb-6 max-w-xs`}>
                Share your experience on Google and help other travellers plan their perfect trip.
              </p>
              <Button
                size="lg"
                onClick={() =>
                  window.open(
                    `https://search.google.com/local/writereview?placeid=${data.place_id}`,
                    '_blank'
                  )
                }
                className="font-semibold text-white hover:opacity-90 transition-opacity"
                style={{
                  background:
                    'linear-gradient(90deg,#4285F4 0%,#EA4335 33%,#FBBC05 66%,#34A853 100%)',
                }}
                data-testid="google-write-review-btn"
              >
                <Star className="w-4 h-4 mr-2 fill-current" />
                Write a Google Review
              </Button>
              <p className={`text-xs mt-4 ${subtle}`}>
                Opens Google in review mode
              </p>
            </article>
          </div>

          {/* Footer: attribution + CTA */}
          <div className="text-center space-y-3">
            <p className={`text-xs ${subtle}`}>
              Shown in Google's relevance order · {data.attribution || 'Reviews from Google'}
            </p>
            {data.google_maps_uri && (
              <a className="vk-btn vk-btn--dark" href={data.google_maps_uri} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-see-all">
                See all reviews on Google <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
